import { CheckIcon, XIcon } from '@phosphor-icons/react'
import { createFormHook, createFormHookContexts, useStore } from '@tanstack/react-form'
import { useCallback, useMemo } from 'react'
import invariant from 'tiny-invariant'
import { match } from 'ts-pattern'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { useToastService } from '../../services/toast-service.tsx'
import { useAllIntegrations, usePostRepositoryIntegration } from '../../utils/api-client/api-hooks.ts'
import { type CreateRepositoryIntegrationRequest, type Tool } from '../../utils/api-client/api-schemas.ts'
import {
  convertIntegrationTypeToTool,
  convertToolToIntegrationType,
  getRepositoryIdentifierLabel,
  getRepositoryIdentifierPlaceholder,
  isMappableIntegration,
  MAPPABLE_INTEGRATION_TYPES,
  type MappableIntegration,
} from '../../utils/repository-utils.tsx'
import { DefaultButton } from '../buttons/default-button.tsx'
import { GhostButton } from '../buttons/ghost-button.tsx'
import {
  IntegrationInstanceSelector,
  type IntegrationInstanceSelection,
} from '../input/integration-instance-selector.tsx'
import { TextInput } from '../input/text-input.tsx'
import * as localStyles from './repository-settings-drawer.css.ts'

// #region Types

export type MapToolIntegrationFormValues = {
  selectedTool: Tool | undefined
  // The specific integration instance chosen in the picker. Distinguishes instances of the same
  // tool type (e.g. two Datadog connections), which `selectedTool` alone cannot.
  selectedIntegrationId: string | undefined
  projectKey: string
}

export type MapToolIntegrationFormProps = {
  repositoryId: string
  onSuccess: () => unknown
  onCancel: () => unknown
  onUploadClick: (e: React.MouseEvent) => unknown
}

// #endregion

// #region Form Hook

const { fieldContext, formContext } = createFormHookContexts()

const { useAppForm, useTypedAppFormContext } = createFormHook({
  fieldComponents: {},
  formComponents: {},
  fieldContext,
  formContext,
})

const FORM_DEFAULT_VALUES: MapToolIntegrationFormValues = {
  selectedTool: undefined,
  selectedIntegrationId: undefined,
  projectKey: '',
}

export const useMapToolIntegrationForm = (defaultValueOverrides: Partial<MapToolIntegrationFormValues> = {}) =>
  useAppForm({
    defaultValues: {
      ...FORM_DEFAULT_VALUES,
      ...defaultValueOverrides,
    },
  })

const useFormContext = () => useTypedAppFormContext({ defaultValues: FORM_DEFAULT_VALUES })

// #endregion

// #region Helpers

const useToolIntegrations = (): MappableIntegration[] => {
  // The same list the selector reads. When these were two different windows, an id the picker
  // offered from page 2 resolved to nothing here.
  const { integrations } = useAllIntegrations()
  return useMemo(() => integrations.filter(isMappableIntegration), [integrations])
}

const buildIntegrationPayload = (
  values: MapToolIntegrationFormValues,
  toolIntegrations: MappableIntegration[]
): CreateRepositoryIntegrationRequest | undefined => {
  const { selectedTool, selectedIntegrationId, projectKey } = values
  // Prefer the explicitly-picked instance; fall back to the first instance of the selected tool
  // type so the tool-type selection path keeps working.
  const integration = selectedIntegrationId
    ? toolIntegrations.find(i => i.id === selectedIntegrationId)
    : selectedTool
      ? toolIntegrations.find(i => convertIntegrationTypeToTool(i.type) === selectedTool)
      : undefined
  if (!integration) {
    return undefined
  }
  return match(integration)
    .with({ type: 'sonar' }, () => ({ type: 'sonar' as const, project_key: projectKey }))
    .with({ type: 'checkmarx' }, () => ({ type: 'checkmarx' as const, project_id: projectKey }))
    .with({ type: 'appscan' }, () => ({ type: 'appscan' as const, application_id: projectKey }))
    .with({ type: 'veracode' }, () => ({ type: 'veracode' as const, application_guid: projectKey }))
    .with({ type: 'arnica' }, () => ({ type: 'arnica' as const, asset_key: projectKey }))
    .with({ type: 'datadog' }, () => ({
      type: 'datadog' as const,
      repository_id: projectKey,
      integration_id: integration.id,
    }))
    .exhaustive()
}

// #endregion

// #region MapToolIntegrationForm

export const MapToolIntegrationForm: React.FC<MapToolIntegrationFormProps> = ({
  repositoryId,
  onSuccess,
  onCancel,
  onUploadClick,
}) => {
  const form = useFormContext()
  const selectedTool = useStore(form.store, state => state.values.selectedTool)
  const selectedIntegrationId = useStore(form.store, state => state.values.selectedIntegrationId)
  const projectKey = useStore(form.store, state => state.values.projectKey)

  const toolIntegrations = useToolIntegrations()

  const { addToast } = useToastService()
  // A repository holds at most one mapping per tool and the endpoint has no PUT, so re-mapping an
  // already-connected tool answers 409. The generic prefix gives the user nothing to act on; naming
  // the cause points at the disconnect-then-reconnect that actually resolves it.
  const showSaveIntegrationError = useShowMutationError({
    prefix: 'Failed to save integration',
    overrides: {
      409: {
        message: 'This repository is already connected to this tool. Disconnect the existing connection first.',
      },
    },
  })
  const postRepositoryIntegration = usePostRepositoryIntegration({
    onError: showSaveIntegrationError,
  })

  // A tool with no user-entered identifier has no field to fill, so it cannot be mapped by hand;
  // `typeFilter` already keeps those out of the picker, and this keeps the two from disagreeing.
  const identifierLabel = getRepositoryIdentifierLabel(selectedTool)

  // Both are required: the payload is built from the chosen *instance*, so a tool without one
  // produced no body and `handleSubmit` returned without a word.
  const isSubmitDisabled = !selectedIntegrationId || !selectedTool || !projectKey

  // `selectedTool` stays in Tool terms because the project-key field is labelled per tool; the picker
  // reports the integration type, so convert on the way in.
  const handleInstanceChange = useCallback(
    ({ type, integrationId }: IntegrationInstanceSelection) => {
      form.setFieldValue('selectedIntegrationId', integrationId)
      form.setFieldValue('selectedTool', type ? convertIntegrationTypeToTool(type) : undefined)
    },
    [form]
  )

  const handleSubmit = useCallback(() => {
    const body = buildIntegrationPayload({ selectedTool, selectedIntegrationId, projectKey }, toolIntegrations)
    // Save is gated on a resolvable instance, so a missing body is a bug rather than a user state.
    invariant(body, 'Cannot save a repository integration without a selected instance')
    postRepositoryIntegration.mutate(
      { repositoryId, body },
      {
        onSuccess: () => {
          addToast({ message: <>Integration saved successfully.</>, variant: 'success' })
          onSuccess()
        },
      }
    )
  }, [
    selectedTool,
    selectedIntegrationId,
    projectKey,
    toolIntegrations,
    postRepositoryIntegration,
    repositoryId,
    addToast,
    onSuccess,
  ])

  return (
    <form
      style={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%' }}
      className={localStyles.mapNewForm}
      onSubmit={e => e.preventDefault()}
    >
      {toolIntegrations.length > 0 ? (
        <IntegrationInstanceSelector
          value={{
            type: selectedTool ? convertToolToIntegrationType(selectedTool) : undefined,
            integrationId: selectedIntegrationId,
          }}
          onChange={handleInstanceChange}
          typeFilter={MAPPABLE_INTEGRATION_TYPES}
          toolLabel="Available tools"
          instanceLabel="Instance"
          isInstanceRequired
        />
      ) : (
        <div className={localStyles.noToolsContainer} data-testid="no-tools-container">
          <div className={localStyles.noToolsContent}>
            <span className={localStyles.noToolsHeading}>No tools have been connected</span>
            <a href="" className={localStyles.uploadLink} onClick={onUploadClick}>
              Upload a SARIF file instead
            </a>
          </div>
        </div>
      )}
      {identifierLabel !== undefined && (
        <TextInput
          id="projectKey"
          label={identifierLabel}
          placeholder={getRepositoryIdentifierPlaceholder(selectedTool)}
          value={projectKey}
          onChange={value => form.setFieldValue('projectKey', value)}
        />
      )}
      <div style={{ display: 'flex', justifyContent: 'end', gap: '16px', width: '100%' }}>
        <GhostButton type="secondary" onClick={onCancel} ariaLabel="Cancel">
          <XIcon size={16} weight="regular" />
        </GhostButton>
        <DefaultButton
          buttonType="primary"
          state={postRepositoryIntegration.isPending ? 'loading' : isSubmitDisabled ? 'disabled' : 'default'}
          onClick={handleSubmit}
          ariaLabel="Save"
        >
          <CheckIcon size={16} weight="regular" />
        </DefaultButton>
      </div>
    </form>
  )
}

// #endregion
